import { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { GoogleTagManager, tracking } from '@iwoca/frontend-tracking-library';
import classNames from 'classnames';

import * as selectors from '../accountsSelector';
import DashboardLoanHeader from '../../sharedDashboardComponents/DashboardLoanHeader';
import LoanCost from '../dashboardComponents/loanCost/LoanCost';
import InterestAccrued from '../OutstandingView/loanInformation/InterestAccrued';
import CurrentPrincipal from '../OutstandingView/loanInformation/CurrentPrincipal';
import OutstandingBalance from '../OutstandingView/loanInformation/OutstandingBalance';
import { EARLY_REPAYMENT_ACCOUNT_ID } from '../../earlyRepayment/EarlyRepayment.actions';
import { REQUEST_CHANGE_ROUTER } from '../../../iwRouter/IwRouter.actions';
import OverdueLoanProgressBar from './OverdueLoanProgressBar';
import CreditLimit from '../OutstandingView/loanInformation/CreditLimit';
import FutureRepayments from '../dashboardComponents/futureRepayment/FutureRepayments';
import PastRepayments from '../dashboardComponents/pastRepayments/PastRepayments';
import FAQs from '../../sharedDashboardComponents/FAQs/FAQs';

import dashboardCtaStyles from '../dashboardComponents/DashboardCTA/DashboardCTA.module.css';
import styles from '../OutstandingView/OutstandingView.module.css';
import questions from './OverdueView.faqs';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { RootState } from '@iwProvider/configureStore';
import { Dispatch } from 'redux';
import { TProductType } from '../../../myAccount/dashboard/Dashboard.types';
import { OverduePayments } from '../dashboardComponents/overduePayments/OverduePayments';

type OwnProps = {
    productType: TProductType;
    accountId: string;
};

const mapStateToProps = (state: RootState, { productType }: OwnProps) => {
    return {
        outstandingBalance: selectors.getOutstandingTotal(state, productType),
        principal: selectors.getOutstandingPrincipal(state, productType),
        overduePaymentAmount: selectors.getNextRepaymentAmount(
            state,
            productType
        ),
        approvalStatusInterestBalance: selectors.getOutstandingInterest(
            state,
            productType
        ),
        approvalStatusFeesBalance: selectors.getOutstandingFees(
            state,
            productType
        ),
        approvalInterestRate: selectors.getMaxMonthlyInterestRate(
            state,
            productType
        )
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    addAccountIdToEarlyRepayment: (accountId: string) =>
        dispatch({
            type: EARLY_REPAYMENT_ACCOUNT_ID,
            accountId
        }),
    goToEarlyRepayment: () =>
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            location: { pathname: '/repayment/' }
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const OverdueView = ({
    overduePaymentAmount,
    outstandingBalance,
    accountId,
    addAccountIdToEarlyRepayment,
    goToEarlyRepayment,
    productType
}: OwnProps & PropsFromRedux) => {
    useEffect(() => {
        tracking.ampli.dashboardViewed({
            account_id: accountId,
            // @ts-expect-error
            product_type: productType,
            dashboard_state: 'overdue'
        });
    }, [accountId, productType]);

    const trackAndGoToEarlyRepayment = useCallback(() => {
        GoogleTagManager.trackClick({
            identifier: 'Make early repayment button'
        });

        addAccountIdToEarlyRepayment(accountId);
        goToEarlyRepayment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            data-testid="overdue-view"
            className={classNames(
                styles.dashboardContainerWithFAQs,
                getCountry() === 'DE' && styles.noFAQs
            )}
        >
            <DashboardLoanHeader
                productType={productType!}
                productInfo={<LoanCost />}
            />
            <div className={styles.loanInformationWrapper}>
                <div className={styles.loanInformation}>
                    <div
                        className={styles.progressBarContainer}
                        data-testid="loan-info"
                    >
                        <div>
                            <OutstandingBalance
                                outstandingBalance={outstandingBalance}
                            />
                        </div>
                        <div className={styles.flexRow}>
                            <div className={styles.flexColumn}>
                                <InterestAccrued />
                                <CurrentPrincipal />
                            </div>
                            <div
                                className={classNames(
                                    styles.flexColumn,
                                    styles.creditLimit
                                )}
                            >
                                <CreditLimit />
                            </div>
                        </div>
                        <div className={styles.flexRow}>
                            <OverdueLoanProgressBar />
                        </div>
                    </div>

                    <OverduePayments
                        overduePaymentAmount={overduePaymentAmount}
                        onClick={trackAndGoToEarlyRepayment}
                    />

                    <FutureRepayments
                        className={dashboardCtaStyles.nonBottomDashboardAction}
                        accountId={accountId}
                    />

                    <PastRepayments accountId={accountId} />
                </div>
                {getCountry() === 'UK' && <FAQs questions={questions} />}
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(OverdueView);
